import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  searchEvents,
  getEvents,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,
  toggleEventActive,
} from "../../../../services/eventsServices";

import { EVENTS_INITIAL_FORM } from "../data/eventsAdminData";

import {
  buildEventsSearchParams,
  getEventsStats,
  normalizeEventForm,
  toLocalInput,
  validateEventForm,
} from "../utils/eventsAdminUtils";

export default function useEventsAdmin() {
  const mountedRef = useRef(true);

  const [q, setQ] = useState("");
  const [categorie, setCategorie] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState(EVENTS_INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});

  const [previewItem, setPreviewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / limit), 1);
  }, [total, limit]);

  const stats = useMemo(() => {
    return getEventsStats(items, total);
  }, [items, total]);

  const showNotice = useCallback((message) => {
    setNotice(message);

    window.clearTimeout(showNotice.timeout);
    showNotice.timeout = window.setTimeout(() => {
      setNotice("");
    }, 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = buildEventsSearchParams({
        q,
        categorie,
        stateFilter,
        dateFrom,
        dateTo,
        page,
        limit,
      });

      const res = await searchEvents(params);

      const hasNoAdminResult =
        !res?.items?.length &&
        !q &&
        !categorie &&
        stateFilter === "all" &&
        !dateFrom &&
        !dateTo &&
        page === 1;

      if (hasNoAdminResult) {
        const pub = await getEvents();
        const fallbackItems = Array.isArray(pub) ? pub : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        return;
      }

      if (!mountedRef.current) return;

      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
    } catch (fetchError) {
      console.error("Erreur chargement événements :", fetchError);

      try {
        const pub = await getEvents();
        const fallbackItems = Array.isArray(pub) ? pub : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        setError(
          "Le chargement administrateur a échoué. Affichage d’une liste de secours.",
        );
      } catch (fallbackError) {
        console.error("Erreur fallback événements :", fallbackError);

        if (!mountedRef.current) return;

        setItems([]);
        setTotal(0);
        setError("Impossible de charger les événements pour le moment.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [q, categorie, stateFilter, dateFrom, dateTo, page, limit]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      if (page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    },
    [fetchData, page],
  );

  const changeStateFilter = useCallback((value) => {
    setStateFilter(value);
    setPage(1);
  }, []);

  const changeCategorie = useCallback((value) => {
    setCategorie(value);
    setPage(1);
  }, []);

  const changeDateFrom = useCallback((value) => {
    setDateFrom(value);
    setPage(1);
  }, []);

  const changeDateTo = useCallback((value) => {
    setDateTo(value);
    setPage(1);
  }, []);

  const changeLimit = useCallback((value) => {
    setLimit(Number(value) || 24);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQ("");
    setCategorie("");
    setStateFilter("all");
    setDateFrom("");
    setDateTo("");
    setLimit(24);
    setPage(1);
  }, []);

  const openCreate = useCallback(() => {
    setCurrentId(null);
    setForm(EVENTS_INITIAL_FORM);
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(async (id) => {
    if (!id) return;

    try {
      setSaving(true);
      setError("");

      const row = await getEventById(id);

      if (!row) {
        setError("Événement introuvable.");
        return;
      }

      setCurrentId(id);
      setForm({
        title: row.title || "",
        subtitle: row.subtitle || "",
        categorie: row.categorie || "",
        description: row.description || "",
        start_date: toLocalInput(row.start_date),
        end_date: toLocalInput(row.end_date),
        photo_vignette: row.photo_vignette || "",
        external_link: row.external_link || "",
        all_day: !!row.all_day,
        is_active: !!row.is_active,
      });
      setFieldErrors({});
      setFormOpen(true);
    } catch (editError) {
      console.error("Erreur ouverture événement :", editError);
      setError("Impossible d’ouvrir cet événement.");
    } finally {
      setSaving(false);
    }
  }, []);

  const openEditFromPreview = useCallback(async () => {
    if (!previewItem?.id) return;

    const id = previewItem.id;
    setPreviewItem(null);
    await openEdit(id);
  }, [openEdit, previewItem]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setCurrentId(null);
    setForm(EVENTS_INITIAL_FORM);
    setFieldErrors({});
    setMediaPickerOpen(false);
  }, []);

  const updateFormField = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    const errors = validateEventForm(form);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = normalizeEventForm(form);

      if (currentId) {
        await updateEvent(currentId, payload);
        showNotice("Événement modifié avec succès.");
      } else {
        await createEvent(payload);
        showNotice("Événement créé avec succès.");
      }

      closeForm();
      await fetchData();
    } catch (saveError) {
      console.error("Erreur enregistrement événement :", saveError);
      setError("Impossible d’enregistrer cet événement.");
    } finally {
      setSaving(false);
    }
  }, [closeForm, currentId, fetchData, form, showNotice]);

  const onToggle = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`toggle-${id}`);
        setError("");

        await toggleEventActive(id);
        showNotice("Statut de publication mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement statut événement :", toggleError);
        setError("Impossible de modifier le statut de cet événement.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const openPreview = useCallback(
    async (id) => {
      if (!id) return;

      const localItem = items.find((item) => item.id === id);

      if (localItem) {
        setPreviewItem(localItem);
        return;
      }

      try {
        setActionId(`preview-${id}`);
        const row = await getEventById(id);

        if (row) {
          setPreviewItem(row);
        }
      } catch (previewError) {
        console.error("Erreur aperçu événement :", previewError);
        setError("Impossible d’afficher l’aperçu de cet événement.");
      } finally {
        setActionId(null);
      }
    },
    [items],
  );

  const closePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  const openDelete = useCallback(
    (id) => {
      const localItem = items.find((item) => item.id === id);

      setDeleteItem(
        localItem || {
          id,
          title: "Cet événement",
        },
      );
    },
    [items],
  );

  const closeDelete = useCallback(() => {
    setDeleteItem(null);
  }, []);

  const onDelete = useCallback(async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);
      setError("");

      await deleteEvent(deleteItem.id);

      setDeleteItem(null);
      showNotice("Événement supprimé.");
      await fetchData();
    } catch (deleteError) {
      console.error("Erreur suppression événement :", deleteError);
      setError("Impossible de supprimer cet événement.");
    } finally {
      setDeleting(false);
    }
  }, [deleteItem, fetchData, showNotice]);

  const openMediaPicker = useCallback(() => {
    setMediaPickerOpen(true);
  }, []);

  const closeMediaPicker = useCallback(() => {
    setMediaPickerOpen(false);
  }, []);

  const handleSelectMedia = useCallback(
    (media) => {
      if (!media) return;

      updateFormField("photo_vignette", media.url || "");
      setMediaPickerOpen(false);
    },
    [updateFormField],
  );

  return {
    q,
    setQ,
    categorie,
    stateFilter,
    dateFrom,
    dateTo,
    page,
    setPage,
    limit,
    items,
    total,
    totalPages,
    stats,

    loading,
    saving,
    deleting,
    actionId,
    error,
    notice,

    formOpen,
    currentId,
    form,
    fieldErrors,
    previewItem,
    deleteItem,
    mediaPickerOpen,

    fetchData,
    onSubmitSearch,
    changeStateFilter,
    changeCategorie,
    changeDateFrom,
    changeDateTo,
    changeLimit,
    resetFilters,

    openCreate,
    openEdit,
    openEditFromPreview,
    closeForm,
    updateFormField,
    onSave,

    onToggle,

    openPreview,
    closePreview,

    openDelete,
    closeDelete,
    onDelete,

    openMediaPicker,
    closeMediaPicker,
    handleSelectMedia,
  };
}